
export type SavedLessonProgress = {
  stepKey: string;
  stageKey: string;
  subIndex: number;
  stageIndex: number;
  curStepItemIndex: number;
  lessonCompleted: boolean;
  progressPercent: number;
  /** Index in `data.timeline` when the lesson uses timeline navigation. */
  timelineIndex?: number;
  /** Sub-step within a rule item (0 = first screen of the rule). */
  ruleStepIndex?: number;
  /** Zepto row id for a rule timeline entry. */
  stepItemId?: number;
};

const KEY_PREFIX = "progress";
const PROGRESS_DIRTY_KEY = "progress_dirty";

function progressStorageKey(bookId: string, exerciseId: string): string {
  return `${KEY_PREFIX}:${`${bookId}`}:${`${exerciseId}`}`;
}

function progressDirtyKey(bookId: string, exerciseId: string): string {
  return `${`${bookId}`}:${`${exerciseId}`}`;
}

function readDirtyManifest(): Record<string, true> {
  try {
    const raw = localStorage.getItem(PROGRESS_DIRTY_KEY);
    if (!raw) {
      return {};
    }
    const data = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, true> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === true) {
        out[key] = true;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeDirtyManifest(manifest: Record<string, true>): void {
  try {
    if (Object.keys(manifest).length === 0) {
      localStorage.removeItem(PROGRESS_DIRTY_KEY);
      return;
    }
    localStorage.setItem(PROGRESS_DIRTY_KEY, JSON.stringify(manifest));
  } catch {
  }
}

export function markLessonProgressDirty(bookId: string, exerciseId: string): void {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return;
  }
  const manifest = readDirtyManifest();
  manifest[progressDirtyKey(bid, eid)] = true;
  writeDirtyManifest(manifest);
}

export function clearLessonProgressDirty(bookId: string, exerciseId: string): void {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return;
  }
  const key = progressDirtyKey(bid, eid);
  const manifest = readDirtyManifest();
  if (!manifest[key]) {
    return;
  }
  delete manifest[key];
  writeDirtyManifest(manifest);
}

export function isLessonProgressDirty(bookId: string, exerciseId: string): boolean {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return false;
  }
  return readDirtyManifest()[progressDirtyKey(bid, eid)] === true;
}

export function listDirtyLessonProgressKeys(): string[] {
  return Object.keys(readDirtyManifest());
}

export function clearAllLessonProgressDirty(): void {
  try {
    localStorage.removeItem(PROGRESS_DIRTY_KEY);
  } catch {
  }
}

function encodeProgressArray(data: SavedLessonProgress): unknown[] {
  const pct = Math.max(0, Math.min(100, Math.round(data.progressPercent || 0)));
  if (data.lessonCompleted) {
    return [1, pct];
  }
  const timelineIndex =
    typeof data.timelineIndex === "number" && Number.isFinite(data.timelineIndex)
      ? Math.floor(data.timelineIndex)
      : -1;
  const ruleStepIndex =
    typeof data.ruleStepIndex === "number" && Number.isFinite(data.ruleStepIndex)
      ? Math.max(0, Math.floor(data.ruleStepIndex))
      : 0;
  const stepItemId =
    typeof data.stepItemId === "number" && Number.isFinite(data.stepItemId) && data.stepItemId > 0
      ? Math.floor(data.stepItemId)
      : 0;
  return [
    0,
    data.stepKey,
    data.stageKey,
    data.subIndex,
    data.stageIndex,
    data.curStepItemIndex,
    pct,
    timelineIndex,
    ruleStepIndex,
    stepItemId,
  ];
}

function decodeProgressArray(raw: unknown): SavedLessonProgress | null {
  if (!Array.isArray(raw) || raw.length < 2) {
    return null;
  }
  const flag = Number(raw[0]);
  if (flag === 1) {
    return {
      stepKey: "",
      stageKey: "",
      subIndex: 0,
      stageIndex: 0,
      curStepItemIndex: 0,
      lessonCompleted: true,
      progressPercent: Math.max(0, Math.min(100, Math.round(Number(raw[1]) || 100))),
    };
  }
  if (raw.length < 7) {
    return null;
  }
  const stepKey = typeof raw[1] === "string" ? raw[1] : "";
  if (!stepKey) {
    return null;
  }
  const stageKey = typeof raw[2] === "string" ? raw[2] : "";
  const subIndex = Number(raw[3]);
  const stageIndex = Number(raw[4]);
  const curStepItemIndex = Number(raw[5]);
  if (!Number.isFinite(subIndex) || !Number.isFinite(stageIndex) || !Number.isFinite(curStepItemIndex)) {
    return null;
  }
  const timelineIndexRaw = raw.length >= 8 ? Number(raw[7]) : -1;
  const ruleStepIndexRaw = raw.length >= 9 ? Number(raw[8]) : 0;
  const stepItemIdRaw = raw.length >= 10 ? Number(raw[9]) : 0;
  const timelineIndex =
    Number.isFinite(timelineIndexRaw) && timelineIndexRaw >= 0 ? Math.floor(timelineIndexRaw) : undefined;
  const ruleStepIndex =
    Number.isFinite(ruleStepIndexRaw) && ruleStepIndexRaw >= 0 ? Math.floor(ruleStepIndexRaw) : undefined;
  const stepItemId =
    Number.isFinite(stepItemIdRaw) && stepItemIdRaw > 0 ? Math.floor(stepItemIdRaw) : undefined;
  return {
    stepKey,
    stageKey,
    subIndex,
    stageIndex,
    curStepItemIndex,
    lessonCompleted: false,
    progressPercent: Math.max(0, Math.min(100, Math.round(Number(raw[6]) || 0))),
    timelineIndex,
    ruleStepIndex,
    stepItemId,
  };
}

function readRawProgress(bookId: string, exerciseId: string): SavedLessonProgress | null {
  const raw = localStorage.getItem(progressStorageKey(bookId, exerciseId)) ?? "";
  if (!raw) {
    return null;
  }
  try {
    return decodeProgressArray(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function readLessonProgressFromStorage(bookId: string, exerciseId: string): SavedLessonProgress | null {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return null;
  }
  return readRawProgress(bid, eid);
}

export function clearLessonProgressFromStorage(bookId: string, exerciseId: string): void {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return;
  }
  try {
    localStorage.removeItem(progressStorageKey(bid, eid));
    clearLessonProgressDirty(bid, eid);
  } catch {

  }
}

export function writeLessonProgressToStorage(
  bookId: string,
  exerciseId: string,
  data: SavedLessonProgress,
  opts?: { fromServer?: boolean },
): void {
  const bid = bookId || "";
  const eid = exerciseId || "";
  if (!bid || !eid) {
    return;
  }
  try {
    const nextRaw = JSON.stringify(encodeProgressArray(data));
    const prevRaw = localStorage.getItem(progressStorageKey(bid, eid));
    if (prevRaw === nextRaw) {
      return;
    }
    localStorage.setItem(progressStorageKey(bid, eid), nextRaw);
    if (!opts?.fromServer) {
      markLessonProgressDirty(bid, eid);
    }
  } catch {

  }
}

export function getSavedLessonProgressPercentFromStorage(bookId: string, exerciseId: string): number {
  const s = readLessonProgressFromStorage(bookId, exerciseId);
  if (!s) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(s.progressPercent || 0)));
}

export function shouldPushLessonProgress(data: SavedLessonProgress): boolean {
  if (data.lessonCompleted) {
    return true;
  }
  if (data.progressPercent > 0) {
    return true;
  }
  if (data.stageIndex > 0) {
    return true;
  }
  if (data.subIndex > 0) {
    return true;
  }
  if ((data.timelineIndex ?? 0) > 0) {
    return true;
  }
  if ((data.ruleStepIndex ?? 0) > 0) {
    return true;
  }
  if ((data.stepItemId ?? 0) > 0) {
    return true;
  }
  if (data.stepKey === "rules") {
    return true;
  }
  return false;
}

export function clearAllLessonProgressFromStorage(): void {
  const prefix = `${KEY_PREFIX}:`;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) {
        keys.push(k);
      }
    }
    for (const k of keys) {
      localStorage.removeItem(k);
    }
    clearAllLessonProgressDirty();
  } catch {

  }
}
